import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { getMyLicence, saveMyLicence, venueImageUrl, VenueLicence } from '../../api';
import { errorMessage, Notice, NoticeBox, PageHead, StatusBadge, usePortal } from './shared';

const LICENCE_STATUS: Record<VenueLicence['status'], string> = { None: 'Not added', Pending: 'In Review', Verified: 'Active', Expired: 'Expired' };

// Liquor licence: needed only for alcohol-related offers, which Cheers2Night shows only while it is
// verified and unexpired. Any change sends it back for verification.
function LicencePanel() {
  const [licence, setLicence] = useState<VenueLicence | null>(null);
  const [form, setForm] = useState({ number: '', expiry: '' });
  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMyLicence().then((l) => { setLicence(l); setForm({ number: l.number ?? '', expiry: l.expiry ?? '' }); }).catch(() => setLicence(null));
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const saved = await saveMyLicence(form.number.trim() || null, form.expiry || null);
      setLicence(saved);
      setNotice({ severity: 'success', text: saved.status === 'Pending' ? 'Licence saved. Cheers2Night will verify it before any alcohol-related offer is shown.' : 'Licence saved.' });
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not save the licence.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="pp-panel" onSubmit={save} style={{ marginTop: 18 }}>
      <div className="pp-strip-head">
        <div><h3 style={{ margin: 0 }}>Liquor Licence</h3><small>Only needed for alcohol or happy-hour offers.</small></div>
        {licence && <StatusBadge status={LICENCE_STATUS[licence.status]} />}
      </div>
      <NoticeBox notice={notice} />
      <div className="pp-row">
        <div className="pp-field"><label htmlFor="licence-number">Licence Number</label><input id="licence-number" className="pp-input" maxLength={60} value={form.number} placeholder="e.g. FL-3 licence number" onChange={(e) => setForm({ ...form, number: e.target.value })} /></div>
        <div className="pp-field"><label htmlFor="licence-expiry">Valid Until</label><input id="licence-expiry" type="date" className="pp-input" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></div>
      </div>
      <p className="pp-hint">Cheers2Night checks the licence against the excise records. Changing it pauses your alcohol-related offers until it's verified again. Keep a copy ready in case we ask for it.</p>
      <div><button type="submit" className="pp-btn pp-gold" disabled={saving || licence === null}>{saving ? 'Saving...' : 'Save Licence'}</button></div>
    </form>
  );
}

// The venue as customers see it. Details are read-only here: there's no self-service edit
// endpoint yet, so changes go through support.
const VenueProfilePage: React.FC = () => {
  const { account, to } = usePortal();
  const cover = venueImageUrl(account?.filePath);

  return (
    <>
      <PageHead title="Venue Profile" subtitle="Your venue's details on Cheers2Night.">
        {account && (
          <a className="pp-btn pp-gold-outline" href={`/venues/resto-${account.restoId}`} target="_blank" rel="noreferrer"><OpenInNewIcon />View Public Page</a>
        )}
      </PageHead>
      <div className="pp-grid2" style={{ alignItems: 'start' }}>
        <section className="pp-panel">
          <h3 style={{ marginTop: 0 }}>Venue Details</h3>
          <div className="pp-narrow" style={{ maxWidth: 'none' }}>
            <div className="pp-field"><label htmlFor="venue-name">Venue Name</label><input id="venue-name" className="pp-input" disabled value={account?.name ?? ''} /></div>
            <div className="pp-field"><label htmlFor="venue-address">Address</label><textarea id="venue-address" className="pp-input" style={{ minHeight: 70 }} disabled value={account?.address ?? ''} /></div>
            <div className="pp-row">
              <div className="pp-field"><label htmlFor="venue-mobile">Mobile</label><input id="venue-mobile" className="pp-input" disabled value={account?.mobile1 ?? ''} /></div>
              <div className="pp-field"><label htmlFor="venue-mobile2">Alternate Mobile</label><input id="venue-mobile2" className="pp-input" disabled value={account?.mobile2 ?? ''} /></div>
            </div>
          </div>
          <p className="pp-hint">To change these details, contact <Link to={to('/partner-account/support')} style={{ color: 'var(--pp-gold)' }}>support</Link>.</p>
          <LicencePanel />
        </section>
        <section className="pp-panel">
          <h3 style={{ marginTop: 0 }}>Cover Photo</h3>
          {cover
            ? <img src={cover} alt={`${account?.name} cover`} style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
            : <p className="pp-muted">No cover photo yet. Your first gallery photo becomes the cover automatically.</p>}
          <Link className="pp-btn pp-outline" style={{ marginTop: 14 }} to={to('/partner-account/gallery')}><PhotoLibraryOutlinedIcon />Manage Image Gallery</Link>
        </section>
      </div>
    </>
  );
};

export default VenueProfilePage;
